/**
 * API Client with Bearer token injection, 401 interception, and
 * exponential backoff retry (1s, 2s, 4s — max 3 attempts).
 *
 * Validates: Requirements 15.3, 15.6
 */

import { getRuntimeConfig } from './runtime-config';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface ApiRequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

interface ApiError {
  status: number;
  message: string;
  errors?: Array<{ field: string; message: string }>;
  code?: string;
  effectiveLimit?: number;
  positionsCreated?: number;
}

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly errors?: Array<{ field: string; message: string }>;
  public readonly code?: string;
  public readonly effectiveLimit?: number;
  public readonly positionsCreated?: number;

  constructor({ status, message, errors, code, effectiveLimit, positionsCreated }: ApiError) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.errors = errors;
    this.code = code;
    this.effectiveLimit = effectiveLimit;
    this.positionsCreated = positionsCreated;
  }
}

const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s
const MAX_ATTEMPTS = 3;

type TokenProvider = () => Promise<string | null>;
type OnUnauthorized = () => void;

let tokenProvider: TokenProvider = async () => null;
let onUnauthorized: OnUnauthorized = () => {};

/**
 * Configure the API client with auth dependencies.
 * Must be called once during app initialization.
 */
export function configureApiClient(options: {
  getToken: TokenProvider;
  onUnauthorized: OnUnauthorized;
}): void {
  tokenProvider = options.getToken;
  onUnauthorized = options.onUnauthorized;
}

function getBaseUrl(): string {
  const config = getRuntimeConfig();
  if (config?.apiBaseUrl) return config.apiBaseUrl;
  return import.meta.env.VITE_API_BASE_URL || '/api';
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryable(status: number): boolean {
  // Retry on server errors (5xx) and rate limiting (429)
  return status >= 500 || status === 429;
}

async function makeRequest<T>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = options;
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${path}`;

  let lastError: ApiClientError | null = null;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    // Wait before retry (skip first attempt)
    if (attempt > 0) {
      await sleep(RETRY_DELAYS[attempt - 1]);
    }

    try {
      const token = await tokenProvider();

      const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers,
      };

      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal,
      });

      // Handle 401 — redirect to login
      if (response.status === 401) {
        onUnauthorized();
        throw new ApiClientError({
          status: 401,
          message: 'Authentication required. Redirecting to login.',
        });
      }

      // Handle non-retryable errors
      if (!response.ok && !isRetryable(response.status)) {
        const errorBody = await response.json().catch(() => ({}));
        throw new ApiClientError({
          status: response.status,
          message: errorBody.message || `Request failed with status ${response.status}`,
          errors: errorBody.errors,
          code: errorBody.code,
          effectiveLimit: errorBody.effectiveLimit,
          positionsCreated: errorBody.positionsCreated,
        });
      }

      // Handle retryable errors
      if (!response.ok && isRetryable(response.status)) {
        lastError = new ApiClientError({
          status: response.status,
          message: `Server error (${response.status}). Retrying...`,
        });
        continue;
      }

      // Success - parse JSON (handle 204 no content)
      if (response.status === 204) {
        return undefined as T;
      }

      return (await response.json()) as T;
    } catch (error) {
      // Don't retry on 401 or non-retryable client errors
      if (error instanceof ApiClientError && !isRetryable(error.status)) {
        throw error;
      }

      // Network errors are retryable
      if (error instanceof TypeError && error.message.includes('fetch')) {
        lastError = new ApiClientError({
          status: 0,
          message: 'Network error. Please check your connection.',
        });
        continue;
      }

      // AbortError should not be retried
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiClientError({
          status: 0,
          message: 'Request was cancelled.',
        });
      }

      // Other unexpected errors - store and retry
      if (error instanceof ApiClientError) {
        lastError = error;
      } else {
        lastError = new ApiClientError({
          status: 0,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }
  }

  // All retries exhausted
  throw (
    lastError ??
    new ApiClientError({
      status: 0,
      message: 'Request failed after maximum retry attempts.',
    })
  );
}

/**
 * API client methods
 */
export const apiClient = {
  get<T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) {
    return makeRequest<T>(path, { ...options, method: 'GET' });
  },

  post<T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) {
    return makeRequest<T>(path, { ...options, method: 'POST', body });
  },

  put<T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) {
    return makeRequest<T>(path, { ...options, method: 'PUT', body });
  },

  delete<T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) {
    return makeRequest<T>(path, { ...options, method: 'DELETE' });
  },

  patch<T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) {
    return makeRequest<T>(path, { ...options, method: 'PATCH', body });
  },
};
